import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core'
import { MessageService, ConfirmationService } from 'primeng/api'
import { ActivatedRoute, Router } from '@angular/router'
import { TabPanel, TabView } from 'primeng/tabview'
import { HttpClient } from '@angular/common/http'
import { Subscription } from 'rxjs'

import { GuarantorContact, guarantorDisplayName, guarantorsOf, MAX_GUARANTORS, relationshipLabel, toGuarantorPayload, withoutGuarantors } from '@data/constants/contact-types'
import { ApiOmnichannelTimelineService } from '@modules/private/omnichannel/data/services/api/api-omnichannel-timeline.service'
import { ChannelSourcesViewService } from '@modules/private/omnichannel/data/services/view/channel-sources-view.service'
import { describeGuarantorError } from '../components/guarantor-contact-dialog/guarantor-contact-dialog.component'
import { CUSTOMER_BILLING_COLUMNS, CUSTOMER_BILLING_ACTIONS } from '../data/constants/customer-billing.constants'
import { ApiCustomerVerificationService } from '../data/services/api/api-customer-verification.service'
import { isCiecExtractionAlive } from '@components/ciec-analysis-view/ciec-analysis-view.component'
import { CiecRequestListItem, ClientCiecStatus } from '@data/interfaces/ciec-request.interface'
import { ApiCustomerBillingService } from '../data/services/api/api-customer-billing.service'
import { ASSETS_COLUMNS } from '@modules/private/provider/data/constants/provider.constants'
import { ClientHubspot, HubspotDealLink } from '../data/interfaces/client-hubspot.interface'
import { PAYMENT_COLUMNS } from '@modules/private/payment/data/constants/payment.constants'
import { CiecTranslateService } from '../../../../shared/services/ciec-translate.service'
import { LEASE_COLUMNS } from '@modules/private/lease/data/contstants/lease.constants'
import { QUOTE_COLUMNS } from '@modules/private/quote/data/constants/quote.constants'
import { SnapshotContext } from '@data/interfaces/verification-snapshot.interface'
import { ApiCustomerService } from '../data/services/api/api-customer.service'
import { ApiPaymentService } from '../data/services/api/api-payment.service'
import { ApiQuotesService } from '../data/services/api/api-quotes.service'
import { ApiAssetsService } from '../data/services/api/api-assets.service'
import { ApiLeaseService } from '../data/services/api/api-lease.service'
import { CustomerService } from '../data/services/view/customer.service'
import { AuthService } from '@data/services/modules/auth/auth.service'
import { environment } from '../../../../../environments/environment'
import { CIEC_MIN_LENGTH } from '@data/constants/ciec.constant'
import { extractApiErrors } from '@data/utils/api-error.util'
import { UserRole } from '@data/enums/user-role.enum'

//* F-025 · HubSpot numera portales y Negocios con enteros. Exigirlo antes de armar el `href` hace que solo
//* un id llegue al enlace (LR-6 de docs/security.md), aunque la API mandara otra cosa.
const HUBSPOT_ID = /^\d+$/
//* Tipo de objeto con el que HubSpot identifica a los Negocios (Deals) en la URL de su ficha.
const HUBSPOT_DEAL_OBJECT_TYPE_ID = '0-3'

@Component({
  selector: 'app-customer-detail',
  templateUrl: './customer-detail.component.html',
  styleUrls: ['./customer-detail.component.scss'],
  providers: [MessageService, ConfirmationService],
})
export class CustomerDetailComponent implements OnInit, OnDestroy {
  id!: string
  client!: any

  //* Plan v4.3 §2b · `?tab=ciec&subtab=…` abre «Análisis Fiscal CIEC» en esa sub-pestaña: la bandeja de Legal y el
  //* renglón de la preoferta llevan aquí. El índice se busca por la referencia del panel porque las pestañas son condicionales,
  //* y se aplica cuando el panel aparece: las pestañas se pintan hasta que llega el cliente, no al iniciar la vista.
  @ViewChild(TabView) private tabView?: TabView
  private ciecTab?: TabPanel
  @ViewChild('ciecTab') private set ciecTabRef(tab: TabPanel | undefined) {
    this.ciecTab = tab
    //* Fuera del ciclo de detección en curso: cambiar la pestaña aquí dispararía ExpressionChanged.
    if (tab) setTimeout(() => this.openCiecTab())
  }
  activeTab = 0
  ciecSubtab: string | null = null
  private wantsCiecTab = false
  private queryParamsSub?: Subscription

  //* La CIEC vive sólo en memoria y sólo mientras se está viendo: no se guarda en el `client`
  //* ni en localStorage. Al salir del detalle desaparece con el componente.
  ciecRevelada = ''
  ciecCargando = false
  leasesColumns = LEASE_COLUMNS
  paymentsColumns = PAYMENT_COLUMNS
  quotesColumns = QUOTE_COLUMNS
  assetsColumns = ASSETS_COLUMNS
  billingColumns = CUSTOMER_BILLING_COLUMNS
  billingActions = CUSTOMER_BILLING_ACTIONS

  // Estado local para billing
  billingData: any[] = []
  billingLoading = false

  // Dialogs para billing
  showBillingFormDialog = false
  billingFormMode: 'create' | 'edit' = 'create'
  selectedBilling: any = null
  selectedLeaseId: string = '' // Cambiar de string | null a string

  // Dialog para edición de cliente
  showCustomerEditDialog = false

  // ADR-0031 · Estado de la pestaña "Análisis Fiscal CIEC"
  // Se carga en el constructor (igual que el comportamiento de pago); PrimeNG 16 NO expone
  // un output (onClick) en p-tabPanel, así que la carga "al abrir el tab"
  // no es viable sin reescribir todo el tabView. Cargar eagerly al construir
  // el detalle del cliente garantiza que al recargar la página los datos
  // están hidratados sin acción del usuario.
  clientVerification: any = null
  isCiecRefreshing = false // Spinner del botón "Actualizar análisis"
  //* Un análisis de Ekatena que YA existe para el RFC de este cliente, si lo hay. Se busca al
  //* cargar la ficha porque preguntarlo es gratis —filtro server-side, sin costo de créditos— y
  //* porque el equipo comercial lleva tiempo corriendo esos análisis fuera de la plataforma.
  ekatenaReport: any = null
  ekatenaBuscando = false
  //* El RFC con el que ya se preguntó. Evita repetir la consulta cada vez que algo refresca la
  //* pantalla, sin necesidad de coordinar quién llama primero.
  private ekatenaRfcBuscado: string | null = null
  ekatenaImportando = false
  isCiecExtractionPending = false // True si extractionStatus ∈ {pending, processing}

  // ADR-0032 · Modal "Solicitar CIEC al cliente"
  showCiecRequestDialog = false

  //* B-075 · Fila «CIEC» de Información Fiscal. La ve quien puede pedirla (`clients.update`) o verla
  //* (`clients.view-ciec`); Contabilidad y Legal no, y sin la fila tampoco se pide el historial.
  canUpdateClient = false
  canViewCiec = false
  canSeeCiecRow = false
  //* La solicitud más reciente, para decir en la fila en qué va sin abrir el modal.
  latestCiecRequest: CiecRequestListItem | null = null
  ciecRequestsState: 'idle' | 'loading' | 'ready' | 'error' = 'idle'
  readonly ciecMinLength = CIEC_MIN_LENGTH

  // ADR-0032 · Modal "Ingresar CIEC" (el interno la captura a mano)
  showCiecEntryDialog = false
  ciecEntryValue = ''

  // "Enviar accesos" · modal aviso → resultado con las credenciales temporales
  showSendAccessDialog = false
  isSendingAccess = false
  accessResult: { emailSent: boolean; emailError?: string; tempPassword: string; loginUsername: string } | null = null

  //! F-158: «Análisis Fiscal CIEC» solo para quien ve Algoritmo (`leases.view`). Contabilidad
  //! abre esta ficha con `clients.get-by-id` y la veía entera, Buró incluido una vez que llegó la foto.
  //! Sin el permiso tampoco se piden la verificación ni el aviso de Ekatena: la pestaña no existe.
  canViewCiecAnalysis = false
  /** F-158 · Contexto de la foto de Syntage para las sub-pestañas del análisis. El cliente nunca se congela. */
  snapshotContext: SnapshotContext | null = null
  /** Plan v4.2 §9.1 · Pestaña «Buró y Círculo»: Admin, Operaciones, Finanzas, Legal y el Seller, que pide el formato. */
  canViewCreditHistory = false

  // Omnicanal · tab Historial + acciones de canal/cierre
  canViewTimeline = false
  //* B-080 · Contabilidad abre esta ficha sin el módulo omnicanal (F-158): sin `omnichannel.get-all` no se
  //* pide el catálogo de canales (`POST /channel-sources` daba 403) ni se pinta «Llegó por», que lo necesita.
  canListChannels = false
  //* B-080 · Mismo permiso que la ruta `/lease/:id` (`app-routing.module.ts`): sin él, el folio va como texto.
  canOpenLease = false
  assignModalVisible = false
  closeModalVisible = false

  // Reasignación de vendedor/broker (solo Admin)
  isAdmin = false
  showReassignModal = false

  //* Plan v4.3 §9.5 · Obligados solidarios: tarjeta propia, separada de «Contactos Adicionales». Se recalculan al
  //* cargar el cliente. Los guarda `PUT /clients/:id/guarantors`, que pide `clients.update` como el resto de la ficha.
  guarantors: GuarantorContact[] = []
  otherContacts: any[] = []
  showGuarantorDialog = false
  editingGuarantor: GuarantorContact | null = null
  removingGuarantorId: string | null = null
  readonly guarantorName = guarantorDisplayName
  readonly maxGuarantors = MAX_GUARANTORS
  readonly guarantorRelationship = relationshipLabel

  constructor(
    private apiCustomerService: ApiCustomerService,
    public paymentsService: ApiPaymentService,
    private activatedRoute: ActivatedRoute,
    public assetsService: ApiAssetsService,
    public quotesService: ApiQuotesService,
    public leaseService: ApiLeaseService,
    public billingService: ApiCustomerBillingService,
    private messageService: MessageService,
    private ciecTranslate: CiecTranslateService,
    private confirmationService: ConfirmationService,
    private router: Router,
    private http: HttpClient,
    public customerService: CustomerService,
    private customerVerificationService: ApiCustomerVerificationService,
    private authService: AuthService,
    private channelSources: ChannelSourcesViewService,
    private omnichannelService: ApiOmnichannelTimelineService,
  ) {
    this.id = this.activatedRoute.snapshot.paramMap.get('id') ?? ''
    //* Antes de `getCustomerData()`: su respuesta llama a `buscarReporteEkatena()`, que consulta el permiso.
    this.canViewCiecAnalysis = this.authService.hasModulePermission('leases', 'view')
    this.snapshotContext = this.canViewCiecAnalysis ? { scope: 'client', id: this.id, frozen: false } : null
    this.canUpdateClient = this.authService.hasModulePermission('clients', 'update')
    this.canViewCiec = this.authService.hasModulePermission('clients', 'view-ciec')
    this.canSeeCiecRow = this.canUpdateClient || this.canViewCiec
    this.getCustomerData()
    if (this.canSeeCiecRow) this.loadLatestCiecRequest()
    // Omnicanal: gating del tab Historial + precarga del catálogo de canales (cache del badge).
    this.canViewTimeline = this.authService.hasModulePermission('omnichannel', 'view')
    this.canViewCreditHistory = this.authService.hasModulePermission('credit-history', 'view')
    // Reasignar vendedor/broker es exclusivo de Admin (patrón isAdmin de wallet-list).
    this.isAdmin = this.authService.getUser?.user?.permissions?.name === UserRole.ADMIN
    this.canListChannels = this.authService.hasModulePermission('omnichannel', 'get-all')
    if (this.canListChannels) this.channelSources.loadAll(1, 100)
    this.canOpenLease = this.authService.hasModulePermission('leases', 'view')
    this.paymentsService.id = this.id
    this.assetsService.id = this.id
    this.quotesService.id = this.id
    this.leaseService.id = this.id
    this.billingService.id = this.id
    //* La calificación se pide al construir: `p-tabPanel` no avisa cuándo se activa.
    this.loadPaymentQuality()
    // ADR-0031: hidratar la verificación CIEC vigente al construir el detalle.
    // El tab "Análisis Fiscal CIEC" no necesita disparar nada al abrirse —
    // los datos ya están listos cuando el usuario llega a la pestaña.
    if (this.canViewCiecAnalysis) this.refreshCiecState()
  }

  ngOnInit() {
    this.queryParamsSub = this.activatedRoute.queryParamMap.subscribe((params) => {
      this.wantsCiecTab = params.get('tab') === 'ciec'
      this.ciecSubtab = this.wantsCiecTab ? params.get('subtab') : null
      this.openCiecTab()
    })
  }

  ngOnDestroy() {
    this.queryParamsSub?.unsubscribe()
  }

  private openCiecTab(): void {
    if (!this.wantsCiecTab || !this.tabView || !this.ciecTab) return
    const index = this.tabView.tabs.indexOf(this.ciecTab)
    if (index >= 0) this.activeTab = index
  }

  formatCurrency(value: number) {
    return new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(value)
  }

  getClientInitials(): string {
    if (!this.client) return ''
    if (this.client.entity === 'Moral') return (this.client.legalName || this.client.name || '').substring(0, 2).toUpperCase()
    const first = (this.client.name || '').charAt(0)
    const last = (this.client.lastName || '').charAt(0)
    return (first + last).toUpperCase()
  }

  getFiscalRegimeLabel(code: string): string {
    if (!code) return 'No especificado'
    const regime = this.customerService.fiscalRegimes.find((r: any) => r.value === code)
    return regime ? regime.label : code
  }

  getStatusSeverity(): string {
    return this.client?.status === 'Active' ? 'success' : 'danger'
  }

  getClientFullName(): string {
    if (!this.client) return ''
    if (this.client.entity === 'Moral') return this.client.legalName || this.client.name
    return [this.client.name, this.client.lastName, this.client.middleName].filter(Boolean).join(' ')
  }

  openAssignModal(): void {
    this.assignModalVisible = true
  }

  openCloseModal(): void {
    this.closeModalVisible = true
  }

  //* Abre el modal Admin de reasignación de vendedor/broker.
  openReassignModal(): void {
    this.showReassignModal = true
  }

  /**
   * F-025 · Un enlace por cada Negocio de HubSpot ligado al cliente. La URL se arma aquí con los ids que
   * manda la API (ADR-0002). Sin portal no hay enlace: la ficha del Negocio vive bajo el portal y sin él
   * HubSpot abre una página de error. Con varios Negocios se numeran para que cada enlace tenga un nombre
   * accesible distinto.
   */
  get hubspotDealLinks(): HubspotDealLink[] {
    const hubspot: ClientHubspot | null | undefined = this.client?.hubspot
    const portalId = hubspot?.portalId
    if (!portalId || !HUBSPOT_ID.test(portalId)) return []
    const dealIds = (hubspot?.dealIds ?? []).filter((dealId) => HUBSPOT_ID.test(dealId))
    return dealIds.map((dealId, index) => ({
      dealId,
      label: dealIds.length === 1 ? 'Ver Negocio en HubSpot' : `Ver Negocio ${index + 1} en HubSpot`,
      url: `https://app.hubspot.com/contacts/${portalId}/record/${HUBSPOT_DEAL_OBJECT_TYPE_ID}/${dealId}`,
    }))
  }

  //* El getter devuelve objetos nuevos en cada ciclo; sin `trackBy` el `*ngFor` rehace los enlaces y les quita el foco.
  trackByDealId(_index: number, link: HubspotDealLink): string {
    return link.dealId
  }

  //* Prospecto "en juego" (aún no ganado ni perdido) → único caso donde aplica "Cierre perdido".
  //* `won` = ya es Cliente (sticky, ADR-0037): NO se le ofrece marcarlo como perdido.
  get isOpenProspect(): boolean {
    const s = this.client?.conversionStatus
    return s === 'in_funnel' || s === 'in_origination'
  }

  /**
   * Pide la CIEC al backend. El botón sólo existe para quien tiene `clients.view-ciec`, pero
   * quien manda es el permiso del endpoint: si alguien llegara aquí sin él, recibe 403.
   *
   * Cada llamada queda registrada del lado del servidor —quién y cuándo—, que es lo que hace
   * aceptable exponer una credencial del SAT.
   */
  revelarCiec(): void {
    if (this.ciecCargando || this.ciecRevelada) return
    this.ciecCargando = true

    this.apiCustomerService.revealCiec(this.id).subscribe({
      next: (r: any) => {
        this.ciecCargando = false
        const ciec = r?.data?.ciec
        if (!ciec) {
          this.messageService.add({ severity: 'warn', summary: 'Sin CIEC', detail: r?.msg ?? 'Este cliente todavía no ha entregado su CIEC.', life: 4000 })
          return
        }
        this.ciecRevelada = ciec
      },
      error: () => {
        this.ciecCargando = false
        this.messageService.add({ severity: 'error', summary: 'No se pudo mostrar', detail: 'Vuelve a intentarlo. Si sigue fallando, revisa tus permisos.', life: 4000 })
      },
    })
  }

  ocultarCiec(): void {
    this.ciecRevelada = ''
  }

  /**
   * B-075 · Si hay CIEC en bóveda y en qué estado está su credencial. Lo manda `GET /clients/:id` en
   * `ciecStatus`; sin él (API anterior a B-075) se asume que no hay, para no ofrecer un «Ver» que
   * respondería 404.
   */
  get ciecStatus(): ClientCiecStatus {
    return this.client?.ciecStatus ?? { stored: false, updatedAt: null, credentialStatus: this.client?.syntage?.credentialStatus ?? null }
  }

  /**
   * Qué dice la fila «CIEC». Con CIEC guardada manda el estado de su credencial; sin ella, la última
   * solicitud por enlace —mientras se lee, `loading`, sin botón: ofrecer «Solicitar CIEC» antes de saber
   * si hay una en curso invitaría a reemplazarla—. Cada caso trae su acción en la plantilla.
   */
  get ciecRowCase(): 'stored' | 'stored-unvalidated' | 'loading' | 'none' | 'pending' | 'pending-sat' | 'exhausted' | 'failed' {
    const status = this.ciecStatus
    if (status.stored) return !status.credentialStatus || status.credentialStatus === 'valid' ? 'stored' : 'stored-unvalidated'
    if (this.ciecRequestsState === 'loading') return 'loading'
    switch (this.latestCiecRequest?.outcome) {
      case 'pending':
      case 'pending_rejected':
        return 'pending'
      case 'received_pending_sat':
        return 'pending-sat'
      case 'exhausted':
        return 'exhausted'
      case 'legacy_rejected':
      case 'legacy_error':
      case 'legacy_failed':
      case 'legacy_no_credential':
        return 'failed'
      default:
        return 'none'
    }
  }

  /**
   * B-075 · «Solicitudes»: el historial y un enlace nuevo también con CIEC guardada (el cliente pudo
   * cambiarla en el SAT). Solo en `stored`: los demás casos ya traen un botón que abre el mismo modal,
   * y en `loading` ninguno, para no ofrecer un enlace antes de saber si hay uno en curso.
   */
  get showCiecRequestsButton(): boolean {
    return this.canUpdateClient && this.ciecRowCase === 'stored'
  }

  get latestCiecRequestLabel(): string {
    return this.ciecTranslate.describeRequestOutcome(this.latestCiecRequest?.outcome).label
  }

  loadLatestCiecRequest(): void {
    this.ciecRequestsState = 'loading'
    this.customerVerificationService.listCiecRequests(this.id).subscribe((r) => {
      if (r?.error) {
        this.latestCiecRequest = null
        this.ciecRequestsState = 'error'
        return
      }
      const list = Array.isArray(r?.data) ? (r.data as CiecRequestListItem[]) : []
      this.latestCiecRequest = list[0] ?? null
      this.ciecRequestsState = 'ready'
    })
  }

  //* Reactiva un cliente caído (lost) → vuelve al funnel para nueva oportunidad/campaña.
  reactivateClient(): void {
    this.omnichannelService.reactivateClient(this.id).subscribe((r: any) => {
      if (!r?.error) {
        this.messageService.add({ severity: 'success', summary: 'Cliente reactivado', detail: 'Vuelve al funnel para una nueva oportunidad de venta.', life: 3000 })
        this.getCustomerData()
      } else {
        this.messageService.add({ severity: 'error', summary: 'Error', detail: r?.msg ?? 'No se pudo reactivar el cliente', life: 4000 })
      }
    })
  }

  // ───────────────────────────────────────────────────────────────────────────
  // Gestión de acceso del cliente (solo Admin — gateado por ['clients','delete']).
  // Botón inteligente: "Borrar cliente" intenta el soft-delete (cascada a cotizaciones);
  // si el backend responde 409 (el cliente tiene arrendamientos), ofrece desactivar el
  // acceso al portal en su lugar (se conserva por historial). Distinto de reactivateClient()
  // (funnel omnicanal lost→in_funnel) — aquí manejamos status Active/Inactive del portal.
  // ───────────────────────────────────────────────────────────────────────────

  //* "Borrar cliente": confirma → DELETE. 409 ⟹ ofrecer desactivar acceso.
  confirmDeleteCustomer(): void {
    this.confirmationService.confirm({
      header: 'Confirmar eliminación',
      message: 'Se eliminará el cliente y todas sus cotizaciones. Esta acción no se puede deshacer.',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Sí, eliminar',
      rejectLabel: 'Cancelar',
      acceptButtonStyleClass: 'p-button-danger',
      accept: () => {
        this.apiCustomerService.deleteClient(this.id).subscribe((r: any) => {
          if (!r.error) {
            this.messageService.add({ severity: 'success', summary: 'Cliente eliminado', detail: 'El cliente y sus cotizaciones fueron eliminados.', life: 3000 })
            this.router.navigate(['/customer'])
            return
          }
          // 409 = tiene arrendamientos → no se puede borrar, solo desactivar.
          if (r.status === 409) {
            this.promptDeactivateAccess()
            return
          }
          this.messageService.add({ severity: 'error', summary: 'Error', detail: r.msg ?? 'No se pudo eliminar el cliente', life: 4000 })
        })
      },
    })
  }

  //* Fallback tras un 409: el cliente tiene arrendamientos → ofrecer desactivar su acceso.
  private promptDeactivateAccess(): void {
    this.confirmationService.confirm({
      header: 'No se puede eliminar',
      message: 'Este cliente tiene arrendamientos y no puede eliminarse (se conserva por historial). ¿Deseas desactivar su acceso al portal?',
      icon: 'pi pi-lock',
      acceptLabel: 'Sí, desactivar acceso',
      rejectLabel: 'Cancelar',
      acceptButtonStyleClass: 'p-button-danger',
      accept: () => this.deactivateAccess(),
    })
  }

  //* POST :id/deactivate → status Inactive (bloquea el ingreso al portal).
  deactivateAccess(): void {
    this.apiCustomerService.deactivate(this.id).subscribe((r: any) => {
      if (!r.error) {
        this.messageService.add({ severity: 'success', summary: 'Acceso desactivado', detail: 'El cliente ya no puede ingresar al portal.', life: 3000 })
        this.getCustomerData()
      } else {
        this.messageService.add({ severity: 'error', summary: 'Error', detail: r.msg ?? 'No se pudo desactivar el cliente', life: 4000 })
      }
    })
  }

  //* "Reactivar acceso": confirma → POST :id/reactivate → status Active.
  confirmReactivateAccess(): void {
    this.confirmationService.confirm({
      header: 'Reactivar acceso',
      message: 'El cliente volverá a poder ingresar al portal. ¿Continuar?',
      icon: 'pi pi-unlock',
      acceptLabel: 'Sí, reactivar',
      rejectLabel: 'Cancelar',
      accept: () => {
        this.apiCustomerService.reactivate(this.id).subscribe((r: any) => {
          if (!r.error) {
            this.messageService.add({ severity: 'success', summary: 'Acceso reactivado', detail: 'El cliente ya puede ingresar al portal.', life: 3000 })
            this.getCustomerData()
          } else {
            this.messageService.add({ severity: 'error', summary: 'Error', detail: r.msg ?? 'No se pudo reactivar el cliente', life: 4000 })
          }
        })
      },
    })
  }

  getCustomerData() {
    this.apiCustomerService.getById(this.id).subscribe((response) => {
      if (!response.error) {
        this.client = response.data
        this.guarantors = guarantorsOf(this.client?.contacts)
        this.otherContacts = withoutGuarantors(this.client?.contacts)
        //* Con el cliente ya cargado hay RFC aunque no exista ninguna verificación.
        this.buscarReporteEkatena()
      } else {
        console.error(response.msg)
      }
    })
  }

  openGuarantorDialog(guarantor: GuarantorContact | null): void {
    this.editingGuarantor = guarantor
    this.showGuarantorDialog = true
  }

  onGuarantorSaved(): void {
    this.messageService.add({ severity: 'success', summary: 'Obligado guardado', detail: 'Se actualizaron los obligados solidarios del cliente.', life: 3000 })
    this.getCustomerData()
  }

  //* La API exige al menos un obligado una vez registrados: con uno solo, «Quitar» queda deshabilitado con su razón.
  confirmRemoveGuarantor(guarantor: GuarantorContact): void {
    if (this.guarantors.length <= 1) return
    this.confirmationService.confirm({
      header: 'Quitar obligado solidario',
      message: `Se quitará a ${guarantorDisplayName(guarantor)} de los obligados solidarios del cliente.`,
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Sí, quitar',
      rejectLabel: 'Cancelar',
      acceptButtonStyleClass: 'p-button-danger',
      accept: () => {
        const remaining = this.guarantors.filter((item) => item.id !== guarantor.id).map((item) => toGuarantorPayload(item))
        this.removingGuarantorId = guarantor.id
        this.apiCustomerService.updateGuarantors(this.id, remaining).subscribe((response) => {
          this.removingGuarantorId = null
          if (!response.error) {
            this.messageService.add({ severity: 'success', summary: 'Obligado quitado', detail: `${guarantorDisplayName(guarantor)} ya no es obligado solidario del cliente.`, life: 3000 })
            this.getCustomerData()
            return
          }
          this.messageService.add({ severity: 'error', summary: 'No se pudo quitar', detail: describeGuarantorError(response), life: 5000 })
        })
      },
    })
  }

  // Cargar datos de billing
  loadBillingData() {
    this.billingLoading = true
    this.billingService.getAll().subscribe({
      next: (response) => {
        this.billingLoading = false
        if (!response.error) {
          this.billingData = response.data || []
        } else {
          this.billingData = []
          console.error(response.msg)
        }
      },
      error: (error) => {
        this.billingLoading = false
        this.billingData = []
        console.error('Error loading billing data:', error)
      },
    })
  }

  // Métodos para billing
  createBilling() {
    this.billingFormMode = 'create'
    this.selectedBilling = null
    this.selectedLeaseId = '' // Cambiar de null a string vacío
    this.showBillingFormDialog = true
  }

  editBilling(billing: any) {
    this.billingFormMode = 'edit'
    this.selectedBilling = billing
    this.selectedLeaseId = billing.leaseId || '' // Cambiar de null a string vacío
    this.showBillingFormDialog = true
  }

  saveBilling(billingData: any) {
    if (this.billingFormMode === 'create') {
      this.createBillingRecord(billingData)
    } else {
      this.updateBillingRecord(billingData)
    }
  }

  private createBillingRecord(billingData: any) {
    this.billingLoading = true

    // Usar el servicio correcto de billing
    this.http.post(`${environment.apiUrl}/billing/create`, billingData).subscribe({
      next: (response: any) => {
        this.billingData.push(response)
        this.showBillingFormDialog = false
        this.messageService.add({
          severity: 'success',
          summary: 'Éxito',
          detail: 'Configuración de facturación creada exitosamente',
        })
        this.billingLoading = false
      },
      error: (error) => {
        console.error('Error creating billing:', error)
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: error.error?.message || 'Error al crear la configuración de facturación',
        })
        this.billingLoading = false
      },
    })
  }

  private updateBillingRecord(billingData: any) {
    this.billingLoading = true

    this.http.put(`${environment.apiUrl}/billing/${this.selectedBilling._id}`, billingData).subscribe({
      next: (response: any) => {
        const index = this.billingData.findIndex((b) => b._id === this.selectedBilling._id)
        if (index !== -1) this.billingData[index] = response
        this.showBillingFormDialog = false
        this.messageService.add({
          severity: 'success',
          summary: 'Éxito',
          detail: 'Configuración de facturación actualizada exitosamente',
        })
        this.billingLoading = false
      },
      error: (error) => {
        console.error('Error updating billing:', error)
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: error.error?.message || 'Error al actualizar la configuración de facturación',
        })
        this.billingLoading = false
      },
    })
  }

  closeBillingDialog() {
    this.showBillingFormDialog = false
    this.selectedBilling = null
    this.selectedLeaseId = '' // Cambiar de null a string vacío
  }

  toggleBillingStatus(billing: any) {
    // Implementar lógica para activar/desactivar billing
    const updatedData = { ...billing, isActive: !billing.isActive }
    this.updateBillingRecord(updatedData)
  }

  // Manejar acciones de tabla
  handleBillingAction(event: any) {
    const { action, data } = event

    switch (action) {
      case 'EDIT':
        this.editBilling(data)
        break
      case 'TOGGLE':
        this.toggleBillingStatus(data)
        break
      case 'VIEW':
        // Implementar vista si es necesario
        break
    }
  }

  // Métodos auxiliares para billing cards
  getBillingInitials(billing: any): string {
    return billing.businessName
      .split(' ')
      .map((word: string) => word.charAt(0))
      .join('')
      .substring(0, 2)
      .toUpperCase()
  }

  getBillingAvatarColor(billing: any): string {
    const colors = ['#667eea', '#764ba2', '#f093fb', '#4facfe', '#43e97b']
    const index = billing.businessName.length % colors.length
    return colors[index]
  }

  getBillingCardClass(billing: any): string {
    return billing.isActive ? 'billing-card-active' : 'billing-card-inactive'
  }

  getPaymentMethodLabel(paymentMethod: string): string {
    const methods: { [key: string]: string } = {
      '01': 'Efectivo',
      '02': 'Cheque nominativo',
      '03': 'Transferencia electrónica de fondos',
      '04': 'Tarjeta de crédito',
      '05': 'Monedero electrónico',
      '06': 'Dinero electrónico',
      '08': 'Vales de despensa',
      '12': 'Dación en pago',
      '13': 'Pago por subrogación',
      '14': 'Pago por consignación',
      '15': 'Condonación',
      '17': 'Compensación',
      '23': 'Novación',
      '24': 'Confusión',
      '25': 'Remisión de deuda',
      '26': 'Prescripción o caducidad',
      '27': 'A satisfacción del acreedor',
      '28': 'Tarjeta de débito',
      '29': 'Tarjeta de servicios',
      '30': 'Aplicación de anticipos',
      '31': 'Intermediario pagos',
      '99': 'Por definir',
    }
    return methods[paymentMethod] || `Código ${paymentMethod}`
  }

  getPaymentTypeLabel(paymentType: string): string {
    const types: { [key: string]: string } = {
      PUE: 'Pago en una sola exhibición',
      PPD: 'Pago en parcialidades o diferido',
    }
    return types[paymentType] || paymentType
  }

  getPaymentTypeClass(paymentType: string): string {
    return paymentType === 'PUE' ? 'p-tag-success' : 'p-tag-info'
  }

  getCfdiUsageLabel(cfdiUsage: string): string {
    const usages: { [key: string]: string } = {
      G01: 'Adquisición de mercancías',
      G02: 'Devoluciones, descuentos o bonificaciones',
      G03: 'Gastos en general',
      I01: 'Construcciones',
      I02: 'Mobilario y equipo de oficina por inversiones',
      I03: 'Equipo de transporte',
      I04: 'Equipo de computo y accesorios',
      I05: 'Dados, troqueles, moldes, matrices y herramental',
      I06: 'Comunicaciones telefónicas',
      I07: 'Comunicaciones satelitales',
      I08: 'Otra maquinaria y equipo',
      D01: 'Honorarios médicos, dentales y gastos hospitalarios',
      D02: 'Gastos médicos por incapacidad o discapacidad',
      D03: 'Gastos funerales',
      D04: 'Donativos',
      D05: 'Intereses reales efectivamente pagados por créditos hipotecarios',
      D06: 'Aportaciones voluntarias al SAR',
      D07: 'Primas por seguros de gastos médicos',
      D08: 'Gastos de transportación escolar obligatoria',
      D09: 'Depósitos en cuentas para el ahorro, primas que tengan como base planes de pensiones',
      D10: 'Pagos por servicios educativos',
      P01: 'Por definir',
      S01: 'Sin efectos fiscales',
      CN01: 'Nómina',
    }
    return usages[cfdiUsage] || `Código ${cfdiUsage}`
  }

  formatDate(date: string | Date): string {
    if (!date) return 'N/A'
    const dateObj = new Date(date)
    return dateObj.toLocaleDateString('es-MX', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    })
  }

  // Métodos para edición de cliente
  editCustomer() {
    if (this.client) {
      // Cargar datos en el servicio
      this.customerService.loadEntityForEdit(this.client)
      this.showCustomerEditDialog = true
    }
  }

  saveCustomer() {
    const data = this.customerService.entity.value

    this.apiCustomerService.update(this.id, data).subscribe((r: any) => {
      if (r.error) {
        const detalles = extractApiErrors(r, 'No se pudo actualizar el cliente. Revisa los campos e inténtalo de nuevo.')
        return this.messageService.addAll(detalles.map((detail) => ({ severity: 'error', summary: 'Revisa los campos', detail, life: 6000 })))
      }

      this.messageService.add({
        severity: 'success',
        summary: 'Éxito',
        detail: `Cliente ${data.name} actualizado correctamente`,
      })

      this.hideCustomerEditDialog()
      this.getCustomerData() // Recargar datos
    })
  }

  hideCustomerEditDialog() {
    this.showCustomerEditDialog = false
  }

  // ─── ADR-0031 · Análisis Fiscal CIEC del cliente ────────────────────────

  private refreshCiecState(): void {
    this.customerVerificationService.getLatest(this.id).subscribe((r: any) => {
      if (r?.error) {
        // No es un error duro: simplemente no hay verificación todavía.
        this.clientVerification = null
        this.isCiecExtractionPending = false
        //! También aquí. Un cliente sin verificación es JUSTO el caso donde el aviso de Ekatena más
        //! rinde, y salir temprano lo dejaba fuera.
        this.buscarReporteEkatena()
        return
      }
      this.clientVerification = r?.data?.verification ?? null
      // Una extracción que arrancó hace mucho se da por colgada y deja de bloquear:
      // el backend la dispara con un evento en memoria del pod, así que un reinicio
      // a media extracción deja el registro en `processing` para siempre.
      this.isCiecExtractionPending = isCiecExtractionAlive(this.clientVerification)
      this.buscarReporteEkatena()
    })
  }

  /**
   * Averigua si hay un análisis de Ekatena esperando para este RFC.
   *
   * No bloquea nada ni propaga error: si el proveedor no responde, la ficha simplemente no ofrece
   * la importación. Un aviso que no aparece es mejor que una ficha que no carga.
   */
  private buscarReporteEkatena(): void {
    //* El RFC de la verificación primero: es el que de verdad se consultó. El del cliente sirve
    //* cuando todavía no hay ninguna verificación, que es justo el caso donde más rinde el aviso.
    if (!this.canViewCiecAnalysis) return
    const rfc = this.clientVerification?.rfc || (this.client as any)?.rfc
    //! Se llama desde DOS sitios y a propósito: `refreshCiecState()` corre al construir la pantalla,
    //! pero `this.client` lo llena otra petición que puede llegar después. Con una sola llamada, un
    //! cliente sin verificación se quedaba sin RFC y el aviso no salía nunca — que era justo el caso
    //! que más importaba. Gana quien tenga el dato primero y el guard evita la consulta repetida.
    if (!rfc) return
    if (this.ekatenaRfcBuscado === rfc) return
    this.ekatenaRfcBuscado = rfc
    this.ekatenaBuscando = true
    //! El `error` NO es opcional aquí: sin él, una consulta que falla deja `ekatenaBuscando` en
    //! true para siempre y el indicador de búsqueda gira sin fin. Antes no se notaba porque no se
    //! pintaba nada mientras buscaba; ahora sí se pinta.
    this.customerVerificationService.buscarReporteEkatena(rfc).subscribe({
      next: (r: any) => {
        this.ekatenaBuscando = false
        this.ekatenaReport = r ?? null
      },
      error: () => {
        this.ekatenaBuscando = false
        this.ekatenaReport = null
        //* Se permite reintentar: el guard por RFC bloquearía el segundo intento para siempre.
        this.ekatenaRfcBuscado = null
      },
    })
  }

  /**
   * Trae el análisis de Ekatena. **Aditivo**: no reemplaza el que ya existe, agrega otro con su
   * procedencia, y los dos quedan en el historial.
   */
  importarEkatena(): void {
    if (this.ekatenaImportando) return
    this.ekatenaImportando = true
    this.customerVerificationService.importarEkatena(this.id).subscribe((r: any) => {
      this.ekatenaImportando = false
      if (r?.error) {
        this.messageService.add({ severity: 'warn', summary: 'No se pudo importar', detail: r?.msg || 'Intenta de nuevo en un momento.' })
        return
      }
      this.messageService.add({ severity: 'success', summary: 'Análisis importado', detail: 'El análisis de Ekatena quedó registrado. El anterior sigue en el historial.' })
      //* Se limpia el guard para que la ficha vuelva a evaluar: tras importar, el aviso debe
      //* desaparecer porque el análisis vigente ya es de Ekatena.
      this.ekatenaRfcBuscado = null
      this.refreshCiecState()
    })
  }

  /**
   * Motivo que se muestra cuando el botón de análisis está deshabilitado. En esta
   * pestaña la extracción en curso es la ÚNICA causa, así que el texto puede ser
   * específico. El componente compartido ya sabía pintarlo — simplemente nunca se
   * le pasaba el valor desde aquí.
   */
  get ciecRefreshDisabledReason(): string {
    if (!this.isCiecExtractionPending) return ''
    return 'Hay una extracción en curso para este cliente. Podrás ejecutar un análisis nuevo cuando termine (1–2 minutos).'
  }

  /**
   * Dispara revalidación CIEC (síncrona, 3-10s). El backend corre `verifyCiec`
   * en modo `auto` y reasigna `client.latestVerification`. Refresca la vista
   * y muestra toast con el resultado.
   */
  runClientCiecRefresh(ciec?: string): void {
    if (this.isCiecRefreshing) return
    this.isCiecRefreshing = true
    this.messageService.add({
      severity: 'info',
      summary: 'Procesando',
      detail: 'Ejecutando verificación CIEC con Syntage…',
    })
    this.customerVerificationService.runCiec(this.id, ciec).subscribe((r: any) => {
      this.isCiecRefreshing = false
      // El fallo llega por dos vías distintas y las dos traen el mismo `code`: como error HTTP
      // (validación previa) o como 200 cuyo `result.success` es false (el análisis corrió y no
      // se pudo completar). Antes solo se miraba la primera, así que un rechazo del SAT o una
      // falta de datos caían en el genérico "terminó con estado no exitoso", que no dice ni qué
      // pasó ni qué sigue.
      //* B-075: con CIEC_REJECTED el motivo del SAT llega aparte, en `result.rejectionReason`.
      const fallo = r?.error
        ? { code: r?.code, message: r?.msg, satReason: null }
        : r?.data?.result?.success === false
          ? { code: r.data.result.code, message: r.data.result.message, satReason: r.data.result.rejectionReason ?? null }
          : null

      if (fallo) {
        // La verificación fallida también se persiste y vale la pena mostrarla: deja constancia
        // del intento en la ficha del cliente en vez de dejar a la vista la oferta anterior.
        this.clientVerification = r?.data?.verification ?? this.clientVerification
        this.isCiecExtractionPending = false

        const info = this.ciecTranslate.describeFailure(fallo.code, fallo.message, fallo.satReason)
        this.messageService.add({
          severity: info.severity,
          summary: info.summary,
          detail: info.detail,
          life: info.action === 'none' ? 10000 : 8000,
        })
        // ADR-0032 · Cuando lo que falta es la CIEC del cliente, se abre directamente el modal de
        // magic link: es la acción que resuelve el problema, y hacérsela buscar no aporta nada.
        if (info.action === 'request-ciec') this.openCiecRequestDialog()
        return
      }

      this.clientVerification = r?.data?.verification ?? this.clientVerification
      const status = this.clientVerification?.status
      this.isCiecExtractionPending = false
      this.messageService.add({
        severity: status === 'success' ? 'success' : 'warn',
        summary: status === 'success' ? 'Éxito' : 'Atención',
        detail: status === 'success' ? 'Análisis fiscal actualizado' : 'La verificación terminó con estado no exitoso',
      })
    })
  }

  /**
   * ADR-0032 · Abre el modal "Solicitar CIEC al cliente". Disparado desde el
   * componente `ciec-analysis-view` vía output `(requestCiec)` y desde el
   * fallback de `CIEC_NOT_STORED` en `runClientCiecRefresh()`.
   */
  openCiecRequestDialog(): void {
    this.showCiecRequestDialog = true
  }

  /**
   * ADR-0032 · Abre el modal "Ingresar CIEC". Disparado por el output `(enterCiec)`
   * del `ciec-analysis-view` (botón del empty state). El interno teclea la CIEC del
   * cliente y corre el análisis con ella sin depender de que la meta el cliente.
   */
  openCiecEntryDialog(): void {
    this.ciecEntryValue = ''
    this.showCiecEntryDialog = true
  }

  /**
   * ADR-0032 · Envía la CIEC ingresada a mano → corre la verificación con ese password.
   *
   * B-075: mínimo `CIEC_MIN_LENGTH` caracteres tras recortar espacios, igual que el backend (`normalizeAndValidateCiec`).
   * Con menos el SAT nunca la vería: el servidor respondería `CIEC_INVALID` sin consultarlo.
   */
  submitCiecEntry(): void {
    if (!this.isCiecEntryValid) return //* el botón ya está deshabilitado; guard defensivo
    this.showCiecEntryDialog = false
    this.runClientCiecRefresh(this.ciecEntryValue.trim())
  }

  get isCiecEntryValid(): boolean {
    return (this.ciecEntryValue || '').trim().length >= CIEC_MIN_LENGTH
  }

  /** Abre el modal "Enviar accesos" en el paso de aviso. */
  openSendAccessDialog(): void {
    this.accessResult = null
    this.showSendAccessDialog = true
  }

  /**
   * Genera + envía los accesos: `POST /clients/:id/send-access` resetea la contraseña,
   * manda el correo REGISTER y devuelve la contraseña temporal para mostrarla al seller.
   */
  submitSendAccess(): void {
    if (this.isSendingAccess) return
    this.isSendingAccess = true
    this.apiCustomerService.sendAccess(this.id).subscribe((r: any) => {
      this.isSendingAccess = false
      if (r?.error) {
        this.messageService.add({ severity: 'error', summary: 'Error', detail: r?.msg || 'No se pudieron enviar los accesos', life: 4000 })
        return
      }
      const d = r?.data ?? r
      this.accessResult = {
        emailSent: !!d.emailSent,
        emailError: d.emailError,
        tempPassword: d.tempPassword,
        loginUsername: d.loginUsername,
      }
      this.messageService.add({
        severity: this.accessResult.emailSent ? 'success' : 'warn',
        summary: this.accessResult.emailSent ? 'Accesos enviados' : 'Accesos generados',
        detail: this.accessResult.emailSent ? 'El cliente recibió sus accesos por correo.' : 'No se pudo enviar el correo; comparte la contraseña mostrada.',
        life: 4000,
      })
    })
  }

  /** Copia la contraseña temporal al portapapeles (feedback vía toast). */
  copyAccessPassword(): void {
    if (!this.accessResult?.tempPassword) return
    navigator.clipboard.writeText(this.accessResult.tempPassword)
    this.messageService.add({ severity: 'success', summary: 'Copiado', detail: 'Contraseña copiada al portapapeles', life: 2500 })
  }

  /**
   * ADR-0032 · El modal generó, canceló o reenvió una solicitud: se vuelve a leer la última para que la
   * fila «CIEC» diga en qué va. B-075: sin toast aquí —el modal ya pone el suyo, y el «Te avisaremos…»
   * de antes prometía un aviso que no existe—.
   */
  onCiecRequestChanged(): void {
    if (this.canSeeCiecRow) this.loadLatestCiecRequest()
  }

  /** El RFC inválido se corrige en «Editar cliente»; el modal de CIEC lo pide al cerrarse. */
  onCiecRequestEditClient(): void {
    this.editCustomer()
  }

  // Comportamiento de pago (F39): `undefined` mientras carga, `null` si falló.
  paymentQuality: any = undefined
  paymentQualityError = ''

  loadPaymentQuality(): void {
    this.paymentQuality = undefined
    this.paymentQualityError = ''
    this.apiCustomerService.getPaymentQuality(this.id).subscribe((r: any) => {
      if (r.error) {
        this.paymentQuality = null
        this.paymentQualityError = r.msg || 'No se pudo leer el comportamiento de pago.'
        return
      }
      this.paymentQuality = r.data
    })
  }

  //* «Sin calificación» no es D: un cliente sin letras cubiertas todavía no tiene historial que juzgar.
  gradeSeverity(grade: string | null): 'success' | 'warning' | 'danger' | undefined {
    if (grade === 'A') return 'success'
    if (grade === 'C') return 'warning'
    if (grade === 'D') return 'danger'
    return undefined
  }
}
